import L, {
    type LatLng,
    type LayerGroup,
    type LeafletMouseEvent,
    type Map as LeafletMap,
} from 'leaflet';
import { loadMilSym, type MilSym } from './renderer';
import { TacticalSymbol, type SerializedSymbol } from './TacticalSymbol';
import { SIDC_AXIS_OF_ADVANCE } from './sidc';
import { createSymbol } from './registry';

/**
 * Clicks that land within this window (ms) of a double-click are treated as the
 * double-click's own constituent clicks and dropped, so ending the line never
 * leaves a stray vertex. Deliberate vertex placement is slower than this.
 */
const DBLCLICK_MS = 400;

interface DraftPoint {
    latlng: LatLng;
    /** Wall-clock time the point was placed, used to reject double-click noise. */
    t: number;
}

/**
 * Owns the interactive drawing of tactical graphics on a Leaflet map: event
 * wiring, click / double-click timing, the in-progress preview layer, and
 * edit-mode gating. It is symbol-agnostic — it never touches geometry; each
 * drawn graphic is a {@link TacticalSymbol} that renders itself, resolved from
 * its SIDC via the symbol registry.
 *
 * The tool is **draw-only**: on completion it hands a plain
 * {@link SerializedSymbol} to `onFinish` and forgets it. Finished graphics are
 * owned by `GraphicsLayer`, driven from Redux — so they survive tool switches and
 * remounts, and one arriving over MQTT reaches the screen by the same path as
 * one drawn here.
 *
 * Deliberately framework-free (no React) so it is unit-testable in isolation;
 * `TacticalDrawLayer` is the thin react-leaflet bridge that constructs one and
 * forwards the `active` flag.
 */
export class TacticalDrawTool {
    private readonly previewGroup: LayerGroup = L.layerGroup();

    private milSym: MilSym | null = null;
    private active = false;
    private disposed = false;

    private current: TacticalSymbol | null = null;
    private draft: DraftPoint[] = [];
    private cursor: LatLng | null = null;
    private raf: number | null = null;

    /** SIDC the next draw produces. Set by the toolbox symbol picker. */
    private sidc: string = SIDC_AXIS_OF_ADVANCE;

    constructor(
        private readonly map: LeafletMap,
        /** Called once per completed graphic with its serialisable snapshot. */
        private readonly onFinish: (symbol: SerializedSymbol) => void,
    ) {}

    /** Bind to the map and mount the preview layer. Call once on mount. */
    attach(): void {
        this.previewGroup.addTo(this.map);
        this.map.on('click', this.onClick);
        this.map.on('mousemove', this.onMove);
        this.map.on('dblclick', this.onDblClick);
    }

    /** Unbind and tear down the preview layer. Call on unmount. */
    detach(): void {
        this.disposed = true;
        this.cancelRaf();
        this.map.off('click', this.onClick);
        this.map.off('mousemove', this.onMove);
        this.map.off('dblclick', this.onDblClick);
        this.map.getContainer().classList.remove('ft-drawing-cursor');
        this.previewGroup.remove();
        this.current = null;
        this.draft = [];
    }

    /**
     * Choose which graphic the next draw produces.
     *
     * A change mid-draw (the user picks a parent after placing a vertex, so the
     * line inherits its affiliation) discards the symbol built for the old code
     * and keeps the drafted vertices: only the symbology changed, so the preview
     * should recolour rather than restart.
     */
    setSidc(sidc: string): void {
        if (this.sidc === sidc) return;
        this.sidc = sidc;
        this.current?.clear();
        this.current = null;
        this.scheduleRedraw();
    }

    /**
     * Place the first vertex without a map click.
     *
     * Used when the draw was started *from* something with a position — "draw
     * line" on a marker — so the tail sits on that marker and the user only aims
     * the tip. No-op unless a draw is active and untouched, so it can never
     * disturb a line already being drawn.
     *
     * The seeded vertex is ordinary afterwards: the next click appends to it and
     * a double-click can finish the line from it like any other.
     */
    seedPoint(latlng: LatLng): void {
        if (!this.active || this.draft.length > 0) return;
        this.draft.push({ latlng, t: 0 });
        this.scheduleRedraw();
    }

    /**
     * Enable/disable interaction. Only the drawing cursor and click handling are
     * gated; any half-drawn line is discarded on deactivate.
     */
    setActive(active: boolean): void {
        this.active = active;
        const container = this.map.getContainer();
        if (active) {
            container.classList.add('ft-drawing-cursor');
            this.warm();
        } else {
            container.classList.remove('ft-drawing-cursor');
            this.resetDrawing();
        }
    }

    /** Warm the lazily-loaded renderer as soon as the tool is selected. */
    private warm(): void {
        if (this.milSym) return;
        void loadMilSym().then((m) => {
            if (this.disposed) return;
            this.milSym = m;
            if (this.active) this.scheduleRedraw();
        });
    }

    private newSymbol(): TacticalSymbol | null {
        return createSymbol(this.sidc, this.map, this.milSym!);
    }

    private onClick = (e: LeafletMouseEvent): void => {
        if (!this.active) return;
        this.draft = [...this.draft, { latlng: e.latlng, t: Date.now() }];
        this.scheduleRedraw();
    };

    private onMove = (e: LeafletMouseEvent): void => {
        // Rubber-band only starts after the first point is set.
        if (!this.active || this.draft.length === 0) return;
        this.cursor = e.latlng;
        this.scheduleRedraw();
    };

    private onDblClick = (e: LeafletMouseEvent): void => {
        if (!this.active) return;
        // Drop the click(s) that belong to this double-click, then use the
        // double-click location as the final vertex.
        const now = Date.now();
        const kept = this.draft.filter((p) => now - p.t >= DBLCLICK_MS);
        this.draft = [...kept, { latlng: e.latlng, t: now }];
        void this.finalize();
    };

    private scheduleRedraw(): void {
        if (this.raf != null) return;
        this.raf = requestAnimationFrame(() => {
            this.raf = null;
            this.drawPreview();
        });
    }

    private drawPreview(): void {
        this.previewGroup.clearLayers();
        if (this.draft.length === 0) return;
        const points = this.draft.map((p) => p.latlng);

        // Renderer still loading: a dashed guide line keeps the cursor responsive.
        if (!this.milSym) {
            const guide = this.cursor ? [...points, this.cursor] : points;
            if (guide.length >= 2) {
                L.polyline(guide, { color: '#555', weight: 2, dashArray: '4 6', opacity: 0.9 }).addTo(
                    this.previewGroup,
                );
            }
            return;
        }

        if (!this.current) this.current = this.newSymbol();
        if (!this.current) return; // unknown SIDC — nothing to preview
        this.current.setPoints(points);
        this.current.layer.addTo(this.previewGroup);
        this.current.renderPreview(this.cursor);
    }

    private async finalize(): Promise<void> {
        this.cancelRaf();
        const draft = this.draft;
        this.draft = [];
        this.cursor = null;
        this.previewGroup.clearLayers();

        const points = draft.map((p) => p.latlng);
        const milSym = this.milSym ?? (await loadMilSym());
        if (this.disposed) return;
        this.milSym = milSym;

        const symbol = this.current ?? this.newSymbol();
        this.current = null;
        if (!symbol) return; // unknown SIDC — nothing to draw
        symbol.setPoints(points);
        if (!symbol.isDrawable) {
            symbol.clear();
            return;
        }
        // Hand the snapshot off and drop the symbol: GraphicsLayer renders the
        // persisted graphic, so the tool keeps no state a reload would lose.
        const serialized = symbol.serialize();
        symbol.clear();
        this.onFinish(serialized);
    }

    private resetDrawing(): void {
        this.cancelRaf();
        this.draft = [];
        this.cursor = null;
        this.previewGroup.clearLayers();
        this.current?.clear();
        this.current = null;
    }

    private cancelRaf(): void {
        if (this.raf != null) {
            cancelAnimationFrame(this.raf);
            this.raf = null;
        }
    }
}
